import { evaluateExpression as evaluateExpressionUtil } from "../../../../utils/evaluateExpression";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ModalAtom from "../../../../components/atoms/modal/modal";
import {
    applyAufmassResultRaw,
    markDirtyAfterMutation,
    selectAddedGroupsV2,
    selectCarriedForwardDataV2,
    selectEditingLineV2,
    selectFormDataV2,
    selectPositionUnitMapV2,
    selectSelectedPositionV2,
    selectShowAddLineModalV2,
    setAddLineModal,
    setAufmassError,
} from "../../../../store/slices/aufmassV2Slice";
import { selectCurrentProjectId } from "../../../../store/slices/projectDetailsSlice";
import { useUpdateAufmassLineMutation } from "../../../../store/services/aufmass";
import { formatQuantityToLocale, showToast } from "../../../../utils/method";
import LineEditorV2 from './local/LineEditorV2';
import QuantityDisplayFooterV2 from "./local/QuantityDisplayFooterV2";

// Extract initial form state to a constant
const INITIAL_FORM_STATE = {
    text: "",
    comments: "",
    factorOperation: "",
    manualQuantity: "",
    quantity: "",
    quantityMismatch: false,
    isSubmitting: false,
};

// V2 aufmass utilities (similar to V1 but adapted for V2 structure)
const aufmassUtilsV2 = {
    checkQuantityMismatch: (manual, calculated) => {
        const manualQty = parseFloat(manual) || 0;
        const calculatedQty = parseFloat(calculated) || 0;
        const tolerance = 0.001;
        return Math.abs(manualQty - calculatedQty) > tolerance;
    },

    evaluateExpression: (expression) => evaluateExpressionUtil(expression),

    validateLvQuantityLimit: (
        selectedPosition,
        existingTotal,
        lineTotal,
        originalLineQuantity,
        carriedForwardTotal,
        formatQuantityToLocale,
        t
    ) => {
        if (!selectedPosition?.quantity) return null;

        const positionQuantity = parseFloat(selectedPosition.quantity) || 0;
        const newTotal = existingTotal - originalLineQuantity + lineTotal + carriedForwardTotal;

        if (newTotal > positionQuantity) {
            return t("error-quantity-exceeds-lv", {
                total: formatQuantityToLocale(newTotal),
                limit: formatQuantityToLocale(positionQuantity),
                unit: selectedPosition.unitTag || ""
            });
        }

        return null;
    }
};

function AddLineModalV2() {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Use V2 selectors
    const showAddLineModal = useSelector(selectShowAddLineModalV2);
    const editingLine = useSelector(selectEditingLineV2);
    const selectedPosition = useSelector(selectSelectedPositionV2);
    const formData = useSelector(selectFormDataV2);
    const addedGroups = useSelector(selectAddedGroupsV2);
    const positionUnitMap = useSelector(selectPositionUnitMapV2);
    const carriedForwardData = useSelector(selectCarriedForwardDataV2);
    const projectId = useSelector(selectCurrentProjectId);
    const [updateAufmassLine] = useUpdateAufmassLineMutation();

    const [formState, setFormState] = useState(INITIAL_FORM_STATE);
    const [errors, setErrors] = useState({
        text: "",
        factorOperation: "",
        manualQuantity: "",
    });

    // Add state for modal validation error
    const [modalError, setModalError] = useState("");

    const lineTextRef = useRef(null);
    const isEditing = !!editingLine;

    // Initialize form when modal opens
    useEffect(() => {
        if (showAddLineModal) {
            if (isEditing && editingLine) {
                // Editing existing line
                setFormState({
                    text: editingLine.text,
                    comments: editingLine.comments,
                    factorOperation: editingLine.factorOperation,
                    manualQuantity: editingLine.manualQuantity || 0,
                    quantity: editingLine.quantity,
                    quantityMismatch: aufmassUtilsV2.checkQuantityMismatch(
                        editingLine.manualQuantity,
                        editingLine.quantity,
                    ),
                    isSubmitting: false,
                });
            } else {
                // Adding new line - use the constant
                setFormState(INITIAL_FORM_STATE);
            }

            setErrors({
                text: "",
                factorOperation: "",
                manualQuantity: "",
            });

            // Focus on text field
            setTimeout(() => {
                if (lineTextRef.current) {
                    lineTextRef.current.focus();
                }
            }, 100);
        }
    }, [showAddLineModal, isEditing, editingLine]);

    // Clear form when modal closes
    useEffect(() => {
        if (!showAddLineModal) {
            setFormState(INITIAL_FORM_STATE);
            setErrors({
                text: "",
                factorOperation: "",
                manualQuantity: "",
            });
        }
    }, [showAddLineModal]);

    const handleFieldChange = (index, field, value) => {
        setFormState(prev => ({
            ...prev,
            [field]: value
        }));

        if (field === 'factorOperation') {
            if (value.trim() === "") {
                // Empty formula field - clear both quantities and flags
                setFormState(prev => ({
                    ...prev,
                    quantity: "",
                    manualQuantity: "",
                    quantityMismatch: false,
                }));

                // Clear factorOperation error if it exists
                if (errors.factorOperation) {
                    setErrors(prev => ({
                        ...prev,
                        factorOperation: ""
                    }));
                }
            } else {
                // Non-empty formula - try to evaluate
                const result = aufmassUtilsV2.evaluateExpression(value);

                if (result !== null) {
                    // Valid formula - store the raw calculated value
                    setFormState(prev => ({
                        ...prev,
                        quantity: result,
                        manualQuantity: result,
                        quantityMismatch: false,
                    }));

                    // Clear factorOperation error if it exists
                    if (errors.factorOperation) {
                        setErrors(prev => ({
                            ...prev,
                            factorOperation: ""
                        }));
                    }
                } else {
                    // Invalid formula - clear both quantity and manualQuantity
                    setFormState(prev => ({
                        ...prev,
                        quantity: "",
                        manualQuantity: "",
                        quantityMismatch: false,
                    }));

                    // Set factorOperation error
                    setErrors(prev => ({
                        ...prev,
                        factorOperation: t("error-invalid-formula")
                    }));
                }
            }
        } else if (field === 'manualQuantity') {
            // Check for quantity mismatch
            const manualQty = value || 0;
            const calculatedQty = formState.quantity || 0;

            setFormState(prev => ({
                ...prev,
                quantityMismatch: value.trim() === "" ? false :
                    aufmassUtilsV2.checkQuantityMismatch(manualQty, calculatedQty)
            }));
        }

        // Clear field error when user types (except for factorOperation which has special handling above)
        if (field !== "factorOperation" && errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    // Helper function to calculate existing aufmass total for a position
    const calculateExistingAufmassTotal = useCallback(
        (positionId) => {
            if (!positionId || !addedGroups) return 0;
            const groups = addedGroups[positionId] || [];
            return groups.reduce((sum, group) => {
                return sum + (group.totalQuantity || 0);
            }, 0);
        },
        [addedGroups],
    );

    // Calculate the line total for preview in footer
    const lineTotal = parseFloat(formState.quantity || 0);

    // Calculate what the position total would be with this line
    const existingTotal = calculateExistingAufmassTotal(selectedPosition?.id);
    const originalLineQuantity = isEditing && editingLine ? parseFloat(editingLine.quantity || 0) : 0;

    // Calculate carried forward total
    const carriedForwardTotalQuantity = useMemo(() => {
        return (carriedForwardData[selectedPosition?.id] || []).reduce((sum, line) => {
            return sum + (line.totalQuantity || 0);
        }, 0);
    }, [carriedForwardData, selectedPosition?.id]);

    // Include carried forward data in the position total calculation
    const positionTotal = existingTotal - originalLineQuantity + lineTotal + carriedForwardTotalQuantity;

    // Helper function to validate LV quantity limits
    const validateLvQuantityLimit = () => {
        return aufmassUtilsV2.validateLvQuantityLimit(
            selectedPosition,
            calculateExistingAufmassTotal(selectedPosition?.id) || 0,
            lineTotal,
            isEditing ? originalLineQuantity : 0,
            carriedForwardTotalQuantity || 0,
            formatQuantityToLocale,
            t
        );
    };

    const validateForm = () => {
        let isValid = true;
        const newErrors = {
            text: "",
            factorOperation: "",
            manualQuantity: "",
        };

        // Clear any previous modal error
        setModalError("");

        if (!formState.text.trim()) {
            newErrors.text = t("error-required");
            isValid = false;
        }

        if (!formState.factorOperation.trim()) {
            newErrors.factorOperation = t("error-required");
            isValid = false;
        }

        if (formState.quantity === "") {
            newErrors.factorOperation = t("error-invalid-formula");
            isValid = false;
        }

        if (formState.manualQuantity === null || formState.manualQuantity === undefined) {
            newErrors.manualQuantity = t("error-required");
            isValid = false;
        }

        // Also check LV quantity limit but set it to the modal error instead of field error
        const lvValidationError = validateLvQuantityLimit();
        if (lvValidationError) {
            setModalError(lvValidationError);
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = () => {
        if (!validateForm()) {
            return;
        }

        // Additional validation check before submission
        const lvValidationError = validateLvQuantityLimit();
        if (lvValidationError) {
            // Set as modal error instead of field error
            setModalError(lvValidationError);
            return;
        }

        // Process line data for submission
        const lineData = {
            text: formState.text,
            comments: formState.comments || "",
            factorOperation: formState.factorOperation,
            manualQuantity: formState.manualQuantity,
            quantity: formState.quantity,
        };

        // Submit the line data
        handleAddLineSubmit(lineData);
    };

    const handleAddLineModalClose = useCallback(() => {
        dispatch(setAddLineModal({ show: false }));
    }, [dispatch]);

    const handleAddLineSubmit = useCallback(
        async (line) => {
            setFormState(prev => ({ ...prev, isSubmitting: true }));

            try {
                if (isEditing && editingLine) {
                    const response = await updateAufmassLine({
                        projectId,
                        aufmassId: formData.id,
                        groupId: editingLine.groupId,
                        lineId: editingLine.id,
                        body: { line },
                    }).unwrap();
                    dispatch(applyAufmassResultRaw(response));
                    dispatch(markDirtyAfterMutation());

                    dispatch(setAddLineModal({ show: false }));
                    showToast(t("aufmass.line-updated-successfully"), {
                        type: "success",
                    });
                }
            } catch (error) {
                dispatch(setAufmassError(error?.data?.message || error?.message));
                showToast(
                    error?.data?.message || error?.message ||
                    (isEditing ? t("aufmass.line-update-failed") : t("aufmass.line-add-failed")),
                    { type: "error" }
                );
            } finally {
                setFormState(prev => ({ ...prev, isSubmitting: false }));
            }
        },
        [dispatch, isEditing, editingLine, formData.id, projectId, updateAufmassLine, t],
    );

    const modalBody = (
        <Form>
            {/* Display validation error at the top of the form */}
            {modalError && (
                <div className="alert alert-danger mb-3">{modalError}</div>
            )}
            <LineEditorV2
                line={formState}
                index={0}
                onLineChange={handleFieldChange}
                getLineError={(index, fieldName) => errors[fieldName] || ""}
                showSequenceControls={false}
                autoFocus={true}
                lineRef={lineTextRef}
            />
        </Form>
    );

    return (
        <ModalAtom
            show={showAddLineModal}
            handleClose={handleAddLineModalClose}
            title={
                `${isEditing ? t("aufmass.edit-line") : t("aufmass.add-line")}${selectedPosition?.itemNumber
                    ? ` - ${t("position")} ${selectedPosition.itemNumber}`
                    : ""
                }`
            }
            handleSubmit={handleSubmit}
            size="lg"
            body={modalBody}
            footer={
                <QuantityDisplayFooterV2
                    selectedPosition={selectedPosition}
                    totalQuantity={positionTotal}
                    itemQuantity={lineTotal}
                    itemLabel={t("aufmass.line-quantity")}
                    showMismatchBadge={formState.quantityMismatch}
                    unitTag={positionUnitMap?.[selectedPosition?.id] || selectedPosition?.unitTag || ""}
                />
            }
            customClassName="add-line-modal"
        />
    );
}

export default AddLineModalV2;